import { Fragment, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import styled, { css, keyframes } from "styled-components";
import { useNavigation } from "../../utils/navigation";

import descriptions from "../../descriptions.json";

const fadeInTop = keyframes`
  from {
    clip-path: polygon(0% 0%, 100% 0%, 100% 0%, 0% 0%);
  }
  to {
    clip-path: polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%);
    
  }
`;
const fadeOutBottom = keyframes`
  from {
    clip-path: polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%);
  }
  to {
    clip-path: polygon(0% 0%, 100% 0%, 100% 0%, 0% 0%);
  }
`;
const DropDown = styled.div`
  /* Samo za telefone, primenjuje se display na osnovu $isOpen */
  ${({ $isOpen }) =>
    $isOpen
      ? css`
          display: none; /* Sakrij ako je otvoreno */
        `
      : css`
          display: flex; /* Prikaži ako nije otvoreno */
          align-items: center;
          justify-content: center;
          background-color: var(--bg-100);
        `}
`;
const GridContainer = styled.div`
  width: 100%;
  min-width: 100%;
  height: 100%;
  min-height: 100%;

  overflow-x: hidden;
  position: relative;
  background-color: red;
`;
const GridItems = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  padding-bottom: 0;
`;
const Box = styled.div`
  width: 100%;
  ${({ $isOpen }) =>
    $isOpen
      ? css`
          animation: ${fadeOutBottom} 0.2s ease-in-out both;
        `
      : css`
          animation: ${fadeInTop} 0.3s ease-in-out both;
        `}
`;

const FlexDiv = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 8px;
`;

const ButtonWrapper = styled.div`
  box-shadow: var(--shadow-small);
  border-bottom: 2px solid var(--bg-200);
  width: 100%;
  display: flex;
  justify-content: center;
  background-color: var(--bg-100);
`;
const Button = styled.button`
  text-transform: uppercase;
  font-weight: 600;

  color: var(--text-200);
  border: none;
  border-radius: 0;

  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 94%;
  font-weight: 200;
  padding: 12px 0;
  background-color: var(--bg-100);
  &:hover {
    background-color: var(--bg-100);
  }
  padding: var(--spacing-md) 0;
  font-size: var(--font-size-large);
  text-transform: uppercase;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-family: "Gudea-Regural";
  margin: 0 0 var(--spacing-xl) 0;
`;

const TableCell = styled.td`
  padding: var(--spacing-md);
  border: 1px solid #ddd;
  width: 100%;
  display: grid;
  grid-template-columns: 1fr 1fr;
  @media (min-width: 1025px) {
    grid-template-columns: 1fr 2fr;
  }
`;
const TableTitle = styled.div`
  color: var(--p-color);
  text-transform: uppercase;
`;
const TableText = styled.div`
  font-family: "Oswald-Medium";
  font-size: var(--font-size-large);
`;
const TableRow = styled.tr`
  background-color: var(--bg-200);
  &:nth-child(even) {
    background-color: var(--bg-100);
  }
`;
const CustomLink = styled(Link)`
  font-size: var(--font-size-large);
`;
const SectionContainer = styled.div`
  width: 100%;
  background-color: var(--bg-200);
  //padding: 14px;
  border-radius: 6px;
  padding: var(--spacing-md);

  @media (min-width: 768px) {
    display: flex;
    position: relative; /* Ovo je potrebno za relativno pozicioniranje unutar roditelja */
    justify-content: flex-end;
    flex-direction: column;
  }
`;
const Title = styled.h2``;
const ProductDetails = ({ product }) => {
  const { t, i18n } = useTranslation();
  const { goToCategory, goToShop } = useNavigation();
  const [isOpenBox, setIsOpenBox] = useState([false, true]);
  const [isOpenContainer, setIsOpenContainer] = useState([false, true]);
  const [isBoxAnimating, setIsBoxAnimating] = useState([false, false]);

  const toggleBox = (index) => {
    if (isBoxAnimating[index]) {
      return;
    }

    const updatedIsOpenBox = [...isOpenBox];
    updatedIsOpenBox[index] = !updatedIsOpenBox[index];
    setIsOpenBox(updatedIsOpenBox);

    const updatedIsBoxAnimating = [...isBoxAnimating];
    updatedIsBoxAnimating[index] = true;
    setIsBoxAnimating(updatedIsBoxAnimating);

    setTimeout(
      () => {
        const updatedIsOpenContainer = [...isOpenContainer];
        updatedIsOpenContainer[index] = !updatedIsOpenContainer[index];
        setIsOpenContainer(updatedIsOpenContainer);

        updatedIsBoxAnimating[index] = false;
        setIsBoxAnimating(updatedIsBoxAnimating);
      },
      !isOpenContainer[index] ? 200 : 10
    );
  };

  const { slug } = useParams();
  const [descData, setDescData] = useState(null);

  useEffect(() => {
    const item = descriptions.find((d) => d.slug === slug);
    setDescData(item ?? null);
  }, [slug, descriptions]);

  if (!descData) {
    return <SectionContainer>{t("loading")}</SectionContainer>;
  }

  // 1. Odabir jezika
  const lang = i18n.language.startsWith("sr")
    ? "sr"
    : i18n.language.startsWith("de")
    ? "de"
    : "en";
  const longKey = `long_${lang}_html`;
  const paras = descData[longKey] || descData[`long_${lang}`] || [];

  // 2. Heuristika za naslov
  // Kratke "Kljuc: Vrednost" linije (npr. "Format: Slim", "Caffeine per
  // Pouch: 100.0 mg") ispunjavaju uslov duzine/broja tacaka isto kao pravi
  // naslovi, pa ih eksplicitno izuzimamo - to su stavke specifikacije, ne
  // novi naslov sekcije.
  const isKeyValueLine = (str) => /^[A-Za-z][\w /-]*:\s/.test(str);
  // Sudi po vidljivom tekstu (bez HTML tagova) - inace kratak naslov sa
  // ubačenim <a href="..."> linkom ispadne duži od 60 karaktera zbog
  // markupa i pogrešno se tretira kao sadržaj prethodne sekcije.
  const isTitle = (str) => {
    const plain = str.replace(/<[^>]*>/g, "");
    return (
      plain.length < 60 &&
      (plain.match(/\./g) || []).length < 2 &&
      !isKeyValueLine(plain)
    );
  };

  // 3. Funkcija za čišćenje stringa
  const clean = (str = "") =>
    str
      .replace(/[\r\n]+/g, " ") // zamenjuje newline-ove jednim razmakom
      .replace(/""/g, "") // uklanja duple navodnike
      .trim();

  // 4. Grupisanje u sekcije
  const sections = [];
  let current = null;

  paras.forEach((raw) => {
    const text = clean(raw);
    if (!text) return; // preskači prazne stringove

    if (isTitle(text)) {
      current = { title: text, contents: [] };
      sections.push(current);
    } else if (current) {
      current.contents.push(text);
    } else {
      // Sadrzaj pre prvog prepoznatog naslova (npr. link je bas u prvom
      // pasusu, pa taj pasus vise nije naslov) - ne sme se tiho izbaciti.
      current = { title: "", contents: [text] };
      sections.push(current);
    }
  });
  return (
    <div>
      <GridContainer>
        <GridItems>
          <ButtonWrapper onClick={() => toggleBox(0)}>
            <Button>
              {t("PRODUCT.DESCRIPTION")}
              {isOpenBox[0] ? (
                <svg
                  width="30px"
                  height="30px"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M6 12H18M12 6V18"
                    stroke="var(--text-200)"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              ) : (
                <svg
                  width="30px"
                  height="30px"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M6 12L18 12"
                    stroke="var(--text-200)"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              )}
            </Button>
          </ButtonWrapper>
        </GridItems>
        <DropDown $isOpen={isOpenContainer[0]}>
          <Box $isOpen={isOpenBox[0]}>
            <FlexDiv>
              <SectionContainer>
                {sections.map(({ title, contents }, idx) => (
                  <Fragment key={idx}>
                    <Title dangerouslySetInnerHTML={{ __html: title }} />
                    {contents.map((html, i) => (
                      <Paragraph
                        key={i}
                        dangerouslySetInnerHTML={{ __html: html }}
                      />
                    ))}
                  </Fragment>
                ))}
              </SectionContainer>
            </FlexDiv>
          </Box>
        </DropDown>
        <GridItems>
          <ButtonWrapper onClick={() => toggleBox(1)}>
            <Button>
              {t("PRODUCT.SPECIFICATION")}
              {isOpenBox[1] ? (
                <svg
                  width="30px"
                  height="30px"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M6 12H18M12 6V18"
                    stroke="var(--text-200)"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              ) : (
                <svg
                  width="30px"
                  height="30px"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M6 12L18 12"
                    stroke="var(--text-200)"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              )}
            </Button>
          </ButtonWrapper>
        </GridItems>
        <DropDown $isOpen={isOpenContainer[1]}>
          <Box $isOpen={isOpenBox[1]}>
            {" "}
            {/* Prikažemo sadržaj sekcije */}
            <Table>
              <tbody>
                <TableRow>
                  <TableCell>
                    <TableTitle>{t("PRODUCT.BRAND")}</TableTitle>
                    <TableText>
                      <CustomLink to={goToCategory(product.category_name)}>
                        {product.category_name}
                      </CustomLink>
                    </TableText>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <TableTitle>{t("PRODUCT.FLAVOR")}</TableTitle>
                    <TableText>{product.flavor}</TableText>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <TableTitle>{t("PRODUCT.FORMAT")}</TableTitle>
                    <TableText>{product.format}</TableText>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <TableTitle>{t("PRODUCT.POUCHES_PER_CAN")}</TableTitle>
                    <TableText>{product.pouches_per_can}</TableText>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <TableTitle>{t("PRODUCT.NET_WEIGHT")}</TableTitle>
                    <TableText>{product.net_weight}g</TableText>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <TableTitle>{t("PRODUCT.NICOTINE")}</TableTitle>
                    <TableText>{product.nicotine}MG</TableText>
                  </TableCell>
                </TableRow>
                {product.manufacturer && (
                  <TableRow>
                    <TableCell>
                      <TableTitle>{t("PRODUCT.MANUFACTURER")}</TableTitle>
                      <TableText>{product.manufacturer}</TableText>
                    </TableCell>
                  </TableRow>
                )}
                <TableRow>
                  <TableCell>
                    <TableTitle>{t("PRODUCT.STOCK")}</TableTitle>
                    <TableText>
                      {product.is_in_stock
                        .split("_")
                        .map((word) => word[0].toUpperCase() + word.slice(1))
                        .join(" ")}
                    </TableText>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <TableTitle>{t("PRODUCT.TYPE")}</TableTitle>
                    <TableText>
                      <CustomLink to={goToShop()}>Nicotine pouches</CustomLink>
                    </TableText>
                  </TableCell>
                </TableRow>
              </tbody>
            </Table>
          </Box>
        </DropDown>
      </GridContainer>
    </div>
  );
};

export default ProductDetails;

const Paragraph = styled.p`
  margin-bottom: 16px;
`;
